import { Link } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import { nl } from 'date-fns/locale'
import { FolderOpen, XCircle, ShieldAlert, Activity, Zap } from 'lucide-react'
import { useDashboardOverview } from '@/hooks/useApi'
import { DossierScoresTable } from '@/components/dashboard/DossierScoresTable'
import { Card, CardContent } from '@/components/ui/card'
import { Spinner } from '@/components/ui/spinner'
import { cn } from '@/lib/utils'

/**
 * The four cards used to count integrations — a table that is empty on this
 * install — so the home page opened on "Total Integrations 0" and said nothing
 * about the dossiers, scans and alerts the dashboard exists for. Each card now
 * answers a question you would otherwise go looking for, and links to where
 * you would act on it.
 */

// A beat run happens at least every 5 minutes (the health-check schedule), so
// nothing finishing for half an hour means the scheduler or the workers are
// down. There is no beat heartbeat of its own to read.
const BEAT_STALE_MINUTES = 30

function KpiCard({
  to,
  icon: Icon,
  label,
  value,
  detail,
  tone = 'neutral',
}: {
  to?: string
  icon: typeof FolderOpen
  label: string
  value: React.ReactNode
  detail?: React.ReactNode
  tone?: 'neutral' | 'warn' | 'bad' | 'good'
}) {
  const toneClass = {
    neutral: 'text-foreground',
    good: 'text-green-600 dark:text-green-300',
    warn: 'text-orange-600 dark:text-orange-300',
    bad: 'text-red-600 dark:text-red-300',
  }[tone]

  const card = (
    <Card className={cn('h-full transition-colors', to && 'hover:border-primary')}>
      <CardContent className="py-4">
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Icon className="h-3.5 w-3.5" />
          {label}
        </p>
        <p className={cn('mt-1 text-2xl font-bold tabular-nums', toneClass)}>{value}</p>
        {detail && <p className="mt-0.5 text-xs text-muted-foreground">{detail}</p>}
      </CardContent>
    </Card>
  )

  return to ? <Link to={to}>{card}</Link> : card
}

function ServiceDot({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className={cn('h-1.5 w-1.5 rounded-full', ok ? 'bg-green-500' : 'bg-red-500')} />
      {label}
    </span>
  )
}

export default function Dashboard() {
  const { data, isLoading } = useDashboardOverview()

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner size="lg" />
      </div>
    )
  }

  const system = data?.system
  const staleBeat =
    system?.minutes_since_last_task !== null &&
    system?.minutes_since_last_task !== undefined &&
    system.minutes_since_last_task > BEAT_STALE_MINUTES
  const systemOk = Boolean(system?.database && system?.redis) && !staleBeat

  const alerts = data?.alerts
  const failed = data?.scans.failed_24h ?? 0

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold">Dashboard</h1>
        <p className="text-muted-foreground">Dossiers, scans, alerts en systeemstatus</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          to="/dossiers"
          icon={FolderOpen}
          label="Dossiers"
          value={data?.dossiers.total ?? 0}
          detail={
            data?.dossiers.avg_seo_score !== null && data?.dossiers.avg_seo_score !== undefined
              ? `gem. SEO-score ${data.dossiers.avg_seo_score}`
              : 'nog geen SEO-score'
          }
        />

        <KpiCard
          to="/scans?status=failed"
          icon={XCircle}
          label="Mislukte scans (24u)"
          value={failed}
          tone={failed > 0 ? 'bad' : 'good'}
          detail={
            data?.scans.running
              ? `${data.scans.running} nu bezig of in wachtrij`
              : 'niets in de wachtrij'
          }
        />

        <KpiCard
          to="/alerts"
          icon={ShieldAlert}
          label="Open alerts"
          value={alerts?.actionable ?? 0}
          tone={alerts?.actionable ? 'bad' : 'good'}
          detail={
            <span className="flex flex-wrap items-center gap-2">
              <span>{alerts?.critical ?? 0} kritiek · {alerts?.high ?? 0} hoog</span>
              {(alerts?.kev ?? 0) > 0 && (
                <span className="inline-flex items-center gap-0.5 font-medium text-red-600 dark:text-red-300">
                  <Zap className="h-3 w-3" />
                  {alerts?.kev} KEV
                </span>
              )}
            </span>
          }
        />

        <KpiCard
          icon={Activity}
          label="Systeem"
          value={systemOk ? 'Gezond' : 'Verstoord'}
          tone={systemOk ? 'good' : 'bad'}
          detail={
            <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <ServiceDot ok={Boolean(system?.database)} label="DB" />
              <ServiceDot ok={Boolean(system?.redis)} label="Redis" />
              <ServiceDot ok={!staleBeat} label="Worker" />
              {system?.last_task_at && (
                <span className={cn(staleBeat && 'text-red-600 dark:text-red-300')}>
                  laatste taak{' '}
                  {formatDistanceToNow(new Date(system.last_task_at), {
                    addSuffix: true,
                    locale: nl,
                  })}
                </span>
              )}
            </span>
          }
        />
      </div>

      <DossierScoresTable />
    </div>
  )
}
